import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Loader2, Paperclip, MailSearch, X, ChevronDown, Info } from "lucide-react";
import { api, emailApi, type ArchivedEmail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { searchWindowHint } from "@/lib/email/searchWindowHint";
import { emailSearchMode } from "@/lib/email/searchMode";
import { shortWhen } from "@/lib/email/format";

const SEARCH_LIMIT = 100; // matches the server's p_limit default in search_surfaceable_emails

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
const ALL_TIME_FROM = "2010-01-01";
const DATE_PRESETS = [
  { label: "7 days", from: () => daysAgoIso(7) },
  { label: "30 days", from: () => daysAgoIso(30) },
  { label: "90 days", from: () => daysAgoIso(90) },
  { label: "This year", from: () => `${new Date().getFullYear()}-01-01` },
  { label: "All time", from: () => ALL_TIME_FROM },
] as const;

function shortDate(iso: string | null): string {
  return iso ? shortWhen(iso) : "";
}

export default function EmailSearch() {
  const { toast } = useToast();
  const initialParams = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(() => initialParams.get("q") ?? "");
  const [accountId, setAccountId] = useState<string | null>(() => initialParams.get("accountId"));
  const [submittedQ, setSubmittedQ] = useState(q);
  const [submittedAccountId, setSubmittedAccountId] = useState(accountId);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [fromDate, setFromDate] = useState(() => initialParams.get("from") ?? "");
  const [toDate, setToDate] = useState(() => initialParams.get("to") ?? "");
  const [submittedFrom, setSubmittedFrom] = useState(fromDate);
  const [submittedTo, setSubmittedTo] = useState(toDate);

  // Deep-link from a mailbox group header (Inbox.tsx / InboxOutlook.tsx): narrow displayed
  // results to a single mailbox. Client-side only — the search endpoint itself is not
  // mailbox-scoped, so a very common term's matches for this mailbox could in principle
  // fall outside the returned window (see the truncation banner below).
  const [mailboxFilter, setMailboxFilter] = useState<string | null>(() => initialParams.get("mailbox"));

  const { data: accounts } = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const selectedAccount = (accounts ?? []).find((a) => a.id === accountId);
  const filteredAccounts = (accounts ?? []).filter((a) =>
    !clientSearch.trim() || a.account_name.toLowerCase().includes(clientSearch.toLowerCase()),
  );

  const hasExplicitRange = submittedFrom.trim() !== "" || submittedTo.trim() !== "";
  const mode = emailSearchMode(submittedQ, submittedAccountId, submittedFrom || null, submittedTo || null);
  const canSearch = emailSearchMode(q, accountId, fromDate || null, toDate || null) !== "none";

  const {
    data,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["email-search", submittedQ, submittedAccountId, submittedFrom, submittedTo],
    queryFn: () => emailApi.searchArchive(submittedQ, submittedAccountId, { from: submittedFrom || undefined, to: submittedTo || undefined }),
    enabled: mode !== "none",
  });

  useEffect(() => {
    if (error) toast({ title: "Search failed", description: (error as Error).message, variant: "destructive" });
  }, [error, toast]);

  useEffect(() => {
    // auto-run on mount if any search param was present
    if (initialParams.get("q") || initialParams.get("accountId") || initialParams.get("from") || initialParams.get("to")) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncUrl = (nextQ: string, nextAccountId: string | null, nextFrom: string, nextTo: string) => {
    const params = new URLSearchParams(window.location.search);
    if (nextQ) params.set("q", nextQ); else params.delete("q");
    if (nextAccountId) params.set("accountId", nextAccountId); else params.delete("accountId");
    if (nextFrom) params.set("from", nextFrom); else params.delete("from");
    if (nextTo) params.set("to", nextTo); else params.delete("to");
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  };

  const runSearch = () => {
    const trimmed = q.trim();
    setSubmittedQ(trimmed);
    setSubmittedAccountId(accountId);
    setSubmittedFrom(fromDate);
    setSubmittedTo(toDate);
    syncUrl(trimmed, accountId, fromDate, toDate);
  };

  // "Search all time": bypasses the window ladder with an explicit (very old) from-date,
  // per migration 104 — an explicit p_from/p_to skips the ladder entirely.
  const searchAllTime = () => {
    setFromDate("2010-01-01");
    setSubmittedFrom("2010-01-01");
    setSubmittedTo(toDate);
    syncUrl(q.trim(), accountId, "2010-01-01", toDate);
  };

  const applyPreset = (preset: (typeof DATE_PRESETS)[number]) => {
    const from = preset.from();
    const to = preset.label === "All time" ? toDate || todayIso() : todayIso();
    setFromDate(from);
    setToDate(to);
    if (q.trim() || accountId) {
      setSubmittedQ(q.trim());
      setSubmittedAccountId(accountId);
      setSubmittedFrom(from);
      setSubmittedTo(to);
      syncUrl(q.trim(), accountId, from, to);
    } else {
      syncUrl(q.trim(), accountId, from, to);
    }
  };

  const activePresetLabel = DATE_PRESETS.find((p) => {
    const expectedFrom = p.from();
    const expectedTo = p.label === "All time" ? toDate : todayIso();
    return fromDate === expectedFrom && (p.label === "All time" ? true : toDate === expectedTo);
  })?.label;

  const clearAll = () => {
    setQ("");
    setAccountId(null);
    setFromDate("");
    setToDate("");
    setSubmittedQ("");
    setSubmittedAccountId(null);
    setSubmittedFrom("");
    setSubmittedTo("");
    setMailboxFilter(null);
    window.history.replaceState(null, "", window.location.pathname);
  };

  const clearMailboxFilter = () => {
    setMailboxFilter(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("mailbox");
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  };

  const allResults = useMemo(() => data?.emails ?? [], [data]);
  const results = useMemo(
    () => (mailboxFilter ? allResults.filter((e) => e.mailbox_address === mailboxFilter) : allResults),
    [allResults, mailboxFilter],
  );

  // Window-ladder honesty banner (migration 104): computed off the UNFILTERED result set,
  // since the ladder decision was made server-side over the whole query, not the
  // client-side mailbox narrowing.
  const windowHint = useMemo(
    () => searchWindowHint(allResults, SEARCH_LIMIT, hasExplicitRange),
    [allResults, hasExplicitRange],
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <MailSearch className="h-6 w-6" /> Email Search
        </h1>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Search all archived email…"
              className="pl-8"
            />
          </div>
          <Popover open={clientPickerOpen} onOpenChange={(o) => { setClientPickerOpen(o); if (!o) setClientSearch(""); }}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="sm:w-56 justify-between font-normal">
                <span className={selectedAccount ? "truncate" : "truncate text-muted-foreground"}>
                  {selectedAccount ? selectedAccount.account_name : "All clients"}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {selectedAccount && (
                    <X
                      className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); setAccountId(null); }}
                    />
                  )}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="start">
              <Input
                autoFocus
                placeholder="Search clients…"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="mb-2"
              />
              <div className="max-h-64 overflow-y-auto -mx-2">
                <button
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 rounded text-muted-foreground"
                  onClick={() => { setAccountId(null); setClientPickerOpen(false); }}
                >
                  All clients
                </button>
                {filteredAccounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-3 py-2">No clients found.</p>
                ) : filteredAccounts.map((a) => (
                  <button
                    key={a.id}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 rounded"
                    onClick={() => { setAccountId(a.id); setClientPickerOpen(false); }}
                  >
                    {a.account_name}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-1 flex-wrap">
            {DATE_PRESETS.map((p) => (
              <Button
                key={p.label}
                type="button"
                size="sm"
                variant={activePresetLabel === p.label ? "default" : "outline"}
                className="h-9 px-2.5 text-xs"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm sm:w-40"
            aria-label="From date"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm sm:w-40"
            aria-label="To date"
          />
          <Button onClick={runSearch} disabled={!canSearch}>
            <Search className="h-4 w-4 mr-1.5" /> Search
          </Button>
          {(q || accountId || fromDate || toDate || mailboxFilter) && (
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={clearAll} title="Clear">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {!canSearch && (
          <p className="text-sm text-muted-foreground">Enter a search term, pick a client, or choose a date range.</p>
        )}

        {mailboxFilter && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="gap-1.5">
              Filtered to {mailboxFilter}
              <X className="h-3 w-3 cursor-pointer" onClick={clearMailboxFilter} />
            </Badge>
          </div>
        )}

        {!isFetching && windowHint.truncated && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              Showing the most recent matches{windowHint.oldestIso ? ` (back to ${shortDate(windowHint.oldestIso)})` : ""}.
              Older matches may exist — add a date range or refine your search.
            </div>
            <Button size="sm" variant="outline" className="h-7 shrink-0" onClick={searchAllTime}>
              Search all time
            </Button>
          </div>
        )}

        {isFetching && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isFetching &&
          mode !== "none" &&
          results.length === 0 &&
          mailboxFilter &&
          (allResults.length > 0 || windowHint.truncated) && (
            <div className="text-center py-8 text-muted-foreground space-y-2">
              <p>
                No matches for <span className="font-medium">{mailboxFilter}</span> in the returned window.
                Older matches may exist — try "Search all time" or clear the mailbox filter.
              </p>
              <div className="flex justify-center gap-2">
                <Button size="sm" variant="outline" onClick={searchAllTime}>
                  Search all time
                </Button>
                <Button size="sm" variant="outline" onClick={clearMailboxFilter}>
                  Clear mailbox filter
                </Button>
              </div>
            </div>
          )}

        {!isFetching &&
          mode !== "none" &&
          results.length === 0 &&
          !(mailboxFilter && (allResults.length > 0 || windowHint.truncated)) && (
            <div className="text-center py-8 text-muted-foreground">No results.</div>
          )}

        {!isFetching && mode === "none" && (
          <div className="text-center py-8 text-muted-foreground">
            Search all archived email, or pick a client to narrow.
          </div>
        )}

        {!isFetching && results.length > 0 && (
          <div className="border rounded-lg divide-y">
            {results.map((item: ArchivedEmail) => (
              <div key={item.id} className="px-4 py-3 hover:bg-muted/30">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={item.is_read ? "text-sm" : "text-sm font-bold"}>
                    {item.subject || "(no subject)"}
                  </span>
                  {item.has_attachments && <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />}
                  <span className="text-xs text-muted-foreground ml-auto shrink-0">{shortDate(item.received_at)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {item.from_name || item.from_email}
                  {" · "}
                  <Badge variant="outline" className="text-[10px] align-middle">{item.mailbox_address}</Badge>
                </div>
                {item.body_preview && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.body_preview}</p>
                )}
                {item.matched_accounts && item.matched_accounts.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1.5">
                    {item.matched_accounts.map((acc) => (
                      <Link key={acc.id} href={`/accounts/${acc.id}`}>
                        <Badge variant="secondary" className="text-[10px] cursor-pointer hover:bg-secondary/70">
                          {acc.name}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
